import React, { useState, useEffect, useContext } from 'react';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { GitCompare, FileText, Loader2, ArrowRight, History, AlertTriangle, CheckCircle, Scale } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

export const DocumentComparePage = () => {
  const token = localStorage.getItem('token');
  const [documents, setDocuments] = useState([]);
  const [doc1, setDoc1] = useState('');
  const [doc2, setDoc2] = useState('');
  const [comparisonType, setComparisonType] = useState('full');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    loadDocuments();
    loadHistory();
  }, []);

  const loadDocuments = async () => {
    try {
      const response = await axios.get(`${API}/documents`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDocuments(response.data);
    } catch (error) {
      toast.error('Failed to load documents');
    }
  };

  const loadHistory = async () => {
    try {
      const response = await axios.get(`${API}/documents/comparisons`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHistory(response.data);
    } catch (error) {
      console.error('Failed to load comparison history');
    }
  };

  const compareDocuments = async () => {
    if (!doc1 || !doc2) {
      toast.error('Please select two documents to compare');
      return;
    }
    if (doc1 === doc2) {
      toast.error('Please select different documents');
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      const response = await axios.post(`${API}/documents/compare`, {
        document_id_1: doc1,
        document_id_2: doc2,
        comparison_type: comparisonType
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setResult(response.data);
      loadHistory();
      toast.success('Comparison complete!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Comparison failed');
    } finally {
      setLoading(false);
    }
  };

  const comparisonTypes = [
    { value: 'full', label: 'Full Comparison', icon: Scale, description: 'Compare all aspects' },
    { value: 'key_terms', label: 'Key Terms', icon: FileText, description: 'Terms & conditions' },
    { value: 'dates', label: 'Dates & Timelines', icon: History, description: 'Deadlines & schedules' },
    { value: 'values', label: 'Financial Terms', icon: AlertTriangle, description: 'Prices & payments' }
  ];

  return (
    <div className="p-8 bg-slate-950 min-h-full">
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-white tracking-tight flex items-center gap-3">
          <GitCompare className="text-indigo-400" />
          Document Comparison
        </h1>
        <p className="text-base text-slate-400 mt-2">Compare two documents side-by-side with AI analysis</p>
      </div>

      {/* Selection Panel */}
      <Card className="bg-slate-900/50 border-slate-800 mb-6">
        <CardContent className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-end">
            {/* Document 1 */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Document 1</label>
              <Select value={doc1} onValueChange={setDoc1}>
                <SelectTrigger className="bg-slate-800 border-slate-700 text-white" data-testid="doc1-select">
                  <SelectValue placeholder="Select first document" />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  {documents.map(doc => (
                    <SelectItem key={doc.id} value={doc.id} disabled={doc.id === doc2}>
                      {doc.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Arrow */}
            <div className="hidden md:flex justify-center items-center">
              <ArrowRight className="text-indigo-400 w-8 h-8" />
            </div>

            {/* Document 2 */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Document 2</label>
              <Select value={doc2} onValueChange={setDoc2}>
                <SelectTrigger className="bg-slate-800 border-slate-700 text-white" data-testid="doc2-select">
                  <SelectValue placeholder="Select second document" />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  {documents.map(doc => (
                    <SelectItem key={doc.id} value={doc.id} disabled={doc.id === doc1}>
                      {doc.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Comparison Type */}
          <div className="mt-6">
            <label className="block text-sm font-medium text-slate-300 mb-3">Comparison Type</label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {comparisonTypes.map(type => (
                <button
                  key={type.value}
                  onClick={() => setComparisonType(type.value)}
                  className={`p-4 rounded-lg border transition-all ${
                    comparisonType === type.value
                      ? 'border-indigo-500 bg-indigo-500/10 text-white'
                      : 'border-slate-700 bg-slate-800/50 text-slate-400 hover:border-slate-600'
                  }`}
                >
                  <type.icon className={`w-5 h-5 mb-2 ${comparisonType === type.value ? 'text-indigo-400' : ''}`} />
                  <div className="text-sm font-medium">{type.label}</div>
                  <div className="text-xs text-slate-500 mt-1">{type.description}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Compare Button */}
          <div className="mt-6 flex justify-end">
            <Button
              onClick={compareDocuments}
              disabled={loading || !doc1 || !doc2}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="compare-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Analyzing...
                </>
              ) : (
                <>
                  <GitCompare className="w-4 h-4 mr-2" />
                  Compare Documents
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      {result && (
        <Card className="bg-slate-900/50 border-slate-800 mb-6" data-testid="comparison-result">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle className="text-green-400" />
              Comparison Results
            </CardTitle>
            <CardDescription>
              {result.document_1?.title} vs {result.document_2?.title}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="prose prose-invert max-w-none">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {result.result}
              </ReactMarkdown>
            </div>
          </CardContent>
        </Card>
      )}

      {/* History */}
      {history.length > 0 && (
        <Card className="bg-slate-900/50 border-slate-800">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <History className="text-slate-400" />
                Recent Comparisons
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setShowHistory(!showHistory)}>
                {showHistory ? 'Hide' : 'Show'}
              </Button>
            </div>
          </CardHeader>
          {showHistory && (
            <CardContent>
              <div className="space-y-3">
                {history.slice(0, 10).map(item => (
                  <div key={item.id} className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg">
                    <div>
                      <div className="text-sm text-white">
                        {item.document_title_1} <span className="text-slate-500">vs</span> {item.document_title_2}
                      </div>
                      <div className="text-xs text-slate-500 mt-1">
                        {item.comparison_type} • {new Date(item.created_at).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          )}
        </Card>
      )}
    </div>
  );
};

export default DocumentComparePage;
